import { useState, useRef, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import IpmAiAssistant, { type IpmAiAssistantHandle, type RecognitionPayload } from "@/components/dashboard/IpmAiAssistant";
import TaskFormDialog, { type PrefilledTaskData } from "@/components/planner/TaskFormDialog";
import { fileToBase64, getAnalysisFile } from "@/lib/imageCompression";
import { clearPendingCapture } from "@/lib/pendingCaptureStore";
import { candidatosDe } from "@/lib/pestDiagnosis";
import { leerDesenlace } from "@/lib/pests/desenlaceIA";
import { usePestCropAiEnabled } from "@/hooks/usePestCropAiEnabled";
import { useScanCropChoices } from "@/hooks/useScanCropChoices";
import { eleccionPorDefecto, type EleccionCultivo } from "@/components/pests/LocationCropChips";
import { ScanCropSheet } from "@/components/pests/ScanCropSheet";

const CLAVE_ULTIMA_ELECCION = "ulture.scan.cropChoice";
function ultimaEleccion(): EleccionCultivo | null {
  try {
    const raw = localStorage.getItem(CLAVE_ULTIMA_ELECCION);
    return raw ? (JSON.parse(raw) as EleccionCultivo) : null;
  } catch {
    return null;
  }
}
function guardarEleccion(v: EleccionCultivo) {
  try { localStorage.setItem(CLAVE_ULTIMA_ELECCION, JSON.stringify(v)); } catch { /* sin almacenamiento */ }
}

/**
 * Ulture Assistant chat + in-chat photo analysis + "create task from scan".
 * Extraído de AdvancedFeatures (antes UltureAssistantInline) para poder usarse
 * igual en la página /advanced y embebido en la ventana del micrófono.
 */
export function UltureAssistantChat({
  autoOpenPhoto = false,
  companyId,
  t,
  lang,
  embedded = false,
  onConversationChange,
}: {
  autoOpenPhoto?: boolean;
  companyId?: string;
  t: (k: string, o?: any) => string;
  lang: string;
  /** Dentro de la ventana del micrófono: composer en flujo (no fijo) y sin cromo propio. */
  embedded?: boolean;
  /** Para que la hoja sepa si ya hay conversación y se estire. */
  onConversationChange?: (hasMessages: boolean) => void;
}) {
  const assistantRef = useRef<IpmAiAssistantHandle>(null);
  const [photoOpen, setPhotoOpen] = useState(autoOpenPhoto);
  const [analyzing, setAnalyzing] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [scanPrefilledTask, setScanPrefilledTask] = useState<PrefilledTaskData | null>(null);

  useEffect(() => {
    if (autoOpenPhoto) setPhotoOpen(true);
  }, [autoOpenPhoto]);

  /* «¿DÓNDE Y QUÉ CULTIVO?» (plan cultivos SIEX F7, 05-10-2026). Con la IA guiada por
     cultivo, la foto del asistente —que es el escáner de «Funciones avanzadas»— le dice a
     la IA el cultivo: con UNA ubicación y UN cultivo, sin preguntar; si hay donde elegir,
     una hoja con los chips (lo último usado ya marcado) antes de analizar. Sin elegir
     («No lo sé») o sin ubicación, la función usa los cultivos del sitio o de toda la
     empresa (la unión). Apagado el interruptor, la foto va como siempre. */
  const { on: cropAiOn } = usePestCropAiEnabled();
  const { locations: scanLocations } = useScanCropChoices(cropAiOn);
  const [preguntaCultivo, setPreguntaCultivo] = useState<EleccionCultivo | null>(null);
  const resolverPregunta = useRef<((v: EleccionCultivo | null) => void) | null>(null);
  const preguntarCultivo = useCallback((): Promise<EleccionCultivo | null> => {
    if (!cropAiOn || scanLocations.length === 0) return Promise.resolve(null);
    const porDefecto = eleccionPorDefecto(scanLocations, ultimaEleccion());
    if (scanLocations.length === 1 && scanLocations[0].crops.length <= 1) return Promise.resolve(porDefecto);
    return new Promise((resolve) => {
      resolverPregunta.current = resolve;
      setPreguntaCultivo(porDefecto);
    });
  }, [cropAiOn, scanLocations]);
  const responderCultivo = useCallback((v: EleccionCultivo) => {
    const resolver = resolverPregunta.current;
    resolverPregunta.current = null;
    setPreguntaCultivo(null);
    guardarEleccion(v);
    resolver?.(v);
  }, []);

  /* `pestIdServidor` (plan cultivos SIEX F7, 05-10-2026): con la IA guiada por cultivo la
     función ya dice QUÉ plaga de la empresa es (resuelta en el servidor contra la lista
     del cultivo; sin sitio, contra la unión de los cultivos de la empresa). Si viene, se usa
     esa; si no, se busca como siempre por científico y luego por nombre. */
  const handleCreateTaskFromRecognition = useCallback(async (rec: RecognitionPayload, pestIdServidor: string | null = null) => {
    if (!rec.data || !companyId) return;
    const data = rec.data;
    try {
      let matchedPestId: string | undefined = pestIdServidor ?? undefined;
      const sci = (data.scientificName || "").trim();
      const common = (data.pestName || "").trim();
      const { data: pestRows } = await supabase
        .from("pests")
        .select("id, name, scientific_name")
        .eq("company_id", companyId);
      if (!matchedPestId && pestRows && pestRows.length > 0) {
        const norm = (s: string | null | undefined) => (s ?? "").trim().toLowerCase();
        const bySci = sci ? pestRows.find(p => norm(p.scientific_name) === norm(sci)) : undefined;
        const byName = !bySci && common ? pestRows.find(p => norm(p.name) === norm(common)) : undefined;
        matchedPestId = (bySci ?? byName)?.id;
      }
      let matchedTreatmentId: string | undefined;
      if (matchedPestId) {
        const { data: tpm } = await supabase
          .from("treatment_pest_map")
          .select("treatment_id")
          .eq("pest_id", matchedPestId)
          .limit(1)
          .maybeSingle();
        matchedTreatmentId = tpm?.treatment_id ?? undefined;
      }
      const sev = (data.severity || "").toLowerCase();
      const priority = sev === "critical" || sev === "high" ? "high" : sev === "low" ? "low" : "medium";
      setScanPrefilledTask({
        type: "treatment",
        priority,
        title: t("advanced.taskTitleFromScan", { pest: data.pestName }),
        pest_id: matchedPestId ?? null,
        treatment_id: matchedTreatmentId,
      });
      setTaskDialogOpen(true);
      if (!matchedPestId) toast.info(t("advanced.pestNotInCatalog"));
    } catch (err) {
      console.error("Error preparing task from scan:", err);
      toast.error(t("advanced.taskPrepareError"));
    }
  }, [companyId, t]);

  const handlePhotoSubmit = useCallback(async (files: File[]) => {
    const file = files[0];
    if (!file) return;
    setAnalyzing(true);
    try {
      const eleccion = await preguntarCultivo();
      const analysisFile = await getAnalysisFile(file);
      const base64 = await fileToBase64(analysisFile);
      const cultivo: Record<string, unknown> = {};
      if (eleccion?.locationId) cultivo.location_id = eleccion.locationId;
      if (eleccion?.cropCode != null) cultivo.crop_code = eleccion.cropCode;
      const { data, error } = await supabase.functions.invoke('analyze-pest-image', {
        body: { image: base64, language: lang, ...cultivo },
      });
      if (error) throw error;
      const rec: RecognitionPayload = {
        identified: !!data?.identified,
        data: data?.data,
        candidates: candidatosDe({
          otros: data?.agreement?.alternatives,
          consideradas: data?.data?.alternatives,
          principal: data?.data?.pestName,
        }),
        // Con la lista del cultivo (F7): «sana» / «no se puede saber» y con qué cultivo se miró.
        outcome: typeof data?.outcome === "string" ? data.outcome : null,
        cropCode: typeof data?.candidates?.cropCode === "number" ? data.candidates.cropCode : null,
      };
      const pestIdServidor = typeof data?.data?.pestId === "string" ? (data.data.pestId as string) : null;
      /* «Crear tratamiento» solo para una PLAGA: un auxiliar o algo abiótico no se tratan. */
      const esPlaga = rec.identified && (leerDesenlace({ identified: true, pestName: rec.data?.pestName, outcome: rec.data?.outcome ?? rec.outcome })?.esPlaga ?? true);
      assistantRef.current?.pushRecognitionMessage(rec, esPlaga ? () => handleCreateTaskFromRecognition(rec, pestIdServidor) : undefined);
      /* El aviso dice lo MISMO que la tarjeta: con «no se puede saber» la tarjeta pide otra
         foto y el aviso decía «No se identificó ninguna plaga», que es lo contrario. */
      const sinPlaga = rec.identified ? null : leerDesenlace({ identified: false, outcome: rec.outcome })?.desenlace;
      if (rec.identified) {
        toast.success(t("advanced.analysisComplete"));
      } else if (sinPlaga === "sana") {
        toast.success(t("pests.aiOutcome.healthyTitle", "La planta se ve sana"));
      } else if (sinPlaga === "no_se_sabe") {
        toast.warning(t("pests.aiOutcome.unclearTitle", "La IA no puede saberlo con lo que se ve"));
      } else {
        toast.warning(t("advanced.pestNotIdentified"));
      }
      await clearPendingCapture("pest-scan");
      setPhotoOpen(false);
    } catch (err) {
      console.error("Error analyzing image:", err);
      toast.error(t("advanced.analysisError"));
    } finally {
      setAnalyzing(false);
    }
  }, [lang, t, handleCreateTaskFromRecognition, preguntarCultivo]);

  return (
    <>
      <IpmAiAssistant
        ref={assistantRef}
        embedded={embedded}
        onConversationChange={onConversationChange}
        onAnalyzePhoto={async (file) => {
          await handlePhotoSubmit([file]);
        }}
      />
      <ScanCropSheet
        open={!!preguntaCultivo}
        locations={scanLocations}
        initial={preguntaCultivo ?? { locationId: null, cropCode: null }}
        onDone={responderCultivo}
      />
      <TaskFormDialog
        open={taskDialogOpen}
        onOpenChange={(open) => {
          setTaskDialogOpen(open);
          if (!open) setScanPrefilledTask(null);
        }}
        onSuccess={() => {
          setTaskDialogOpen(false);
          setScanPrefilledTask(null);
          toast.success(t("advanced.taskCreatedFromScan"));
        }}
        prefilledData={scanPrefilledTask}
      />
    </>
  );
}
